// What this item trades at right now, derived from the same rows the Recent
// offers table prints — so the answer is at the top of the page instead of
// somewhere down a scroll of 25 observations.
//
// Outside the component for the reason offersSort.ts records: vitest here has
// no React renderer, so anything left inside a component cannot be tested, and
// a headline price is the worst thing on the page to be quietly wrong.
import type { ItemObservation, PricedMode, Variation } from "../api/types";
import { aged, plural } from "../format";
import { type Spread, spreadOf } from "../market";

/** One side of the market as of the last time anyone looked.
 *
 *  Extends Variation so the figure can say which of the item it is a price
 *  for. An enchanted or augmented listing is a different good at a different
 *  price — that is the whole reason components/Variation.tsx exists — and a
 *  lone headline figure is the one place on the page with no row beside it to
 *  give the difference away. Carrying the three fields lets the caller spread
 *  a Quote straight into <Variation />. */
export interface Quote extends Variation {
  price: number;
  /** Who the figure belongs to: the nick of the row that actually won the
   *  quote, not the scan's newest row and not the first one the filter kept.
   *
   *  Without it the page's two headline figures name a price with nobody
   *  attached to it, and the reader's next move — go and trade with whoever is
   *  offering that — means scrolling the table below hunting for the row the
   *  number came from. */
  nick: string;
  /** The newest observation in the scan quoted — not the whole item's newest,
   *  which may belong to the other side. */
  observed_at: number;
  /** How many offers of this mode that scan held, the quoted one included. */
  count: number;
  /** How many distinct traders those offers came from.
   *
   *  `count` alone cannot tell a market from one person: nine asks are nine
   *  asks whether nine traders made them or one trader listed the same item in
   *  nine slots of one store. The second is a price nobody is competing with,
   *  and it is the reading a trader most wants to distinguish — so the page
   *  says "best of 9 asks from 1 trader" rather than leaving the two states
   *  spelled identically.
   *
   *  Distinct by nick, which is what the rows carry. A nick is the market's own
   *  identifier for a trader — it is what the store window shows and what
   *  traderHref links by — so counting by it is counting the way the reader
   *  counts, and it needs no join. */
  traders: number;
}

export interface ItemQuotes {
  buy: Quote | null;
  sell: Quote | null;
}

/** The spread for the page's two headline figures, or null when either side
 *  has never been seen.
 *
 *  A thin wrapper on `market.ts`'s `spreadOf` and deliberately nothing more.
 *  The sign convention, the percentage's denominator and the zero-ask case all
 *  live there, because the price chart's tooltip computes the same figure per
 *  day and the two must not be able to disagree. What this adds is the only
 *  part that is about *these two quotes*: that a missing side yields no spread
 *  at all rather than half a subtraction.
 */
export function itemSpread(quotes: ItemQuotes): Spread | null {
  if (quotes.buy === null || quotes.sell === null) return null;
  return spreadOf(quotes.buy.price, quotes.sell.price);
}

export function itemQuote(offers: ItemObservation[]): ItemQuotes {
  return { buy: side(offers, "buy"), sell: side(offers, "sell") };
}

function side(offers: ItemObservation[], mode: PricedMode): Quote | null {
  // `price !== null` is type narrowing, not a filter with a case behind it:
  // models.py rejects a priced mode without a price at ingest. It is here so a
  // null can never reach the comparison below, where it would compare as
  // smaller than every real ask and win the quote.
  const rows = offers.filter(
    (o): o is ItemObservation & { price: number } => o.mode === mode && o.price !== null,
  );
  if (rows.length === 0) return null;

  let uid = rows[0].scan_uid;
  let observed_at = rows[0].observed_at;
  for (const row of rows) {
    if (row.observed_at > observed_at) {
      uid = row.scan_uid;
      observed_at = row.observed_at;
    }
  }

  const scan = rows.filter((o) => o.scan_uid === uid);
  // The winning row, not just the winning number: what it was a price *for*
  // travels with it, and so does who offered it. A bid is what someone will
  // pay, so the most of it is best; an ask is what they want for it, so the
  // least — the rule compare.ts's bestFirst states for the sorted tables.
  //
  // Strict `>` / `<`, so a tie goes to whichever row came first in the array
  // and that trader's nick is the one the page names. Several traders sitting
  // at the identical best price is ordinary rather than exceptional, and no
  // tie-break would be truer than another — quoteNote's count ("best of 5
  // bids") is what tells the reader that others stood behind the one named.
  const best = scan.reduce((a, b) =>
    (mode === "buy" ? b.price > a.price : b.price < a.price) ? b : a,
  );
  return {
    price: best.price,
    nick: best.nick,
    enchant: best.enchant,
    element: best.element,
    ability: best.ability,
    observed_at: scan.reduce((newest, o) => Math.max(newest, o.observed_at), observed_at),
    count: scan.length,
    traders: new Set(scan.map((o) => o.nick)).size,
  };
}

/** The line under a headline figure: how old it is, and what it was the best
 *  of. Composed here rather than in format.ts because the two words it chooses
 *  between — a bid and an ask — are this page's reading of buy and sell, not a
 *  formatting rule the rest of the site shares.
 *
 *  Relative, with the exact time going in the element's `title`: the question a
 *  headline price raises is "is this current?", and only an age answers it at a
 *  glance. `now` is injectable for the same reason `ago`'s is.
 *
 *  The count is what stops a single desperate bid from reading as the market,
 *  and the trader count is what stops the count itself from doing the same: one
 *  trader with nine slots open posts nine asks, and "best of 9 asks" alone
 *  reads as nine people competing. "best of 9 asks from 1 trader" is a price
 *  one person is holding up, and it should not look like a market.
 *
 *  Only said when there is more than one offer to attribute. A single bid came
 *  from a single trader necessarily, and "1 bid from 1 trader" spends a clause
 *  restating the one before it. */
export function quoteNote(
  quote: Quote | null,
  mode: PricedMode,
  now: number = Date.now() / 1000,
): string {
  if (quote === null) return "never seen";
  const offers = plural(quote.count, mode === "buy" ? "bid" : "ask");
  const stood = quote.count > 1
    ? `best of ${offers} from ${plural(quote.traders, "trader")}`
    : offers;
  return `${aged(quote.observed_at, now)} · ${stood}`;
}
